import { memo } from 'react';
import { Box, Group, Stack, Text } from '@mantine/core';
import { Activity, Bug, Clock, Cloud, Cpu, Crosshair, DollarSign, Factory, Fingerprint, Network, Siren, Swords } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { Pill } from '../ui/Pill';
import { neutral } from '../../theme/tokens';
import type { Difficulty, ScenarioSummary } from '../../api/types';

/**
 * ScenarioCard — catalog grid tile. Pure presentational; memoized so the grid
 * doesn't re-render every card on filter/search input.
 *
 * The layout is deliberately RIGID. A catalog is scanned in columns, so the
 * eye needs the same information at the same height on every card; a grid where
 * each tile sets its own rhythm reads as unfinished no matter how good the
 * individual card looks. Three fixed bands — title, description, footer — with
 * the footer pinned to the bottom.
 */

const DIFFICULTY_VARIANT: Record<
  Difficulty,
  'success' | 'info' | 'warn' | 'danger'
> = {
  beginner: 'success',
  intermediate: 'info',
  advanced: 'warn',
  expert: 'danger',
};

/** Two lines of the title, so every description starts at the same y. */
const TITLE_BAND = 44;
/** Two lines of description. Three made a card with a 2-line title very tall. */
const DESC_BAND = 32;
/** One row of technique pills, reserved whether or not the card fills it. */
const TECHNIQUE_BAND = 22;

interface ScenarioCardProps {
  scenario: ScenarioSummary;
}

const DOMAIN_ICON: Record<string, LucideIcon> = {
  'threat-hunting': Crosshair,
  'incident-response': Siren,
  'forensics': Fingerprint,
  'malware-analysis': Bug,
  'ot-ics': Factory,
  'soc-ops': Activity,
  'network-defense': Network,
  'cloud-security': Cloud,
  'purple-team': Swords,
};

function domainHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

function DomainGlyph({ domain }: { domain: string }) {
  const Icon = DOMAIN_ICON[domain] ?? Crosshair;
  const hue = domainHue(domain || 'x');
  return (
    <Box
      style={{
        flexShrink: 0,
        width: 40,
        height: 40,
        borderRadius: 10,
        background: `hsl(${hue} 45% 22% / 0.55)`,
        border: `1px solid hsl(${hue} 50% 45% / 0.4)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={20} color={`hsl(${hue} 70% 62%)`} strokeWidth={1.75} />
    </Box>
  );
}

function ScenarioCardInner({ scenario }: ScenarioCardProps) {
  return (
    <Link
      to="/scenarios/$slug"
      params={{ slug: scenario.slug }}
      preload="intent"
      style={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}
    >
      <Card hoverable style={{ height: '100%' }}>
        <Stack gap="sm" style={{ height: '100%' }} justify="space-between">
          <Stack gap="xs">
            <Group justify="space-between" align="start" wrap="nowrap" gap="xs">
              <Group gap={10} wrap="nowrap" align="start" style={{ minWidth: 0 }}>
                <DomainGlyph domain={scenario.domain} />
                <Text
                  fw={700}
                  size="md"
                  lineClamp={2}
                  style={{ letterSpacing: '0.02em', minHeight: TITLE_BAND }}
                >
                  {scenario.name}
                </Text>
              </Group>
              {/*
                flexShrink:0 is load-bearing, not cosmetic. Inside a nowrap flex
                row the badge is a shrinkable item, so a long scenario name
                squeezed it until the label was clipped to "HA…" / "EA…" — the
                difficulty, which is the one thing a learner picks a scenario by,
                rendered as two letters and an ellipsis.
              */}
              <Box style={{ flexShrink: 0 }}>
                <Badge variant={DIFFICULTY_VARIANT[scenario.difficulty]} size="xs">
                  {scenario.difficulty}
                </Badge>
              </Box>
            </Group>
            <Text size="xs" c="dimmed" lineClamp={2} style={{ minHeight: DESC_BAND }}>
              {scenario.description}
            </Text>
          </Stack>

          <Stack gap="sm">
            <Group gap={4} wrap="nowrap" style={{ minHeight: TECHNIQUE_BAND, overflow: 'hidden' }}>
              {scenario.techniques.slice(0, 3).map((t) => (
                <Pill key={t} size="xs" variant="neutral">
                  {t}
                </Pill>
              ))}
              {scenario.techniques.length > 3 ? (
                <Pill size="xs" variant="neutral">
                  +{scenario.techniques.length - 3}
                </Pill>
              ) : null}
            </Group>

            <Group gap="lg" wrap="nowrap">
              <Metric icon={<Cpu size={12} />} label={`${scenario.vmCount} VMs`} />
              <Metric icon={<Clock size={12} />} label={`${scenario.durationMins}m`} />
              <Metric icon={<Crosshair size={12} />} label={`${scenario.objectiveCount} obj`} />
              <Metric
                icon={<DollarSign size={12} />}
                label={`$${scenario.costPerHour.toFixed(2)}/h`}
              />
            </Group>
          </Stack>
        </Stack>
      </Card>
    </Link>
  );
}

function Metric({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <Group gap={4} wrap="nowrap">
      {/* Token, not a hardcoded hex: the old '#71717a' stayed mid-grey in dark
          mode, where it sank into the card and the icons vanished. */}
      <span style={{ color: neutral.iconMuted, display: 'flex' }}>{icon}</span>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Group>
  );
}

export const ScenarioCard = memo(ScenarioCardInner);
